"use client";

import * as React from "react";

import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Button } from "@/components/ui/button";
import { FlagIcon } from "@/components/country/flags";
import { SupportedCountries } from "@/components/country/country";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface CountryComboboxProps {
  value?: string;
  isError?: boolean;
  onValueChange?: (value: string) => void;
}

export function CountryCombobox({
  value,
  isError = false,
  onValueChange,
}: CountryComboboxProps) {
  const { t } = useTranslation(["common"]);
  const [open, setOpen] = React.useState(false);
  const countries = React.useMemo(() => SupportedCountries, []);

  const selectedCountry = countries.find((country) => country.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal bg-transparent truncate text-muted-foreground",
            isError ? "border-destructive" : "",
          )}
        >
          <div className="flex flex-row gap-4">
            {selectedCountry && (
              <FlagIcon countryCodes={[selectedCountry?.code || ""]} />
            )}
            {selectedCountry
              ? selectedCountry.name
              : t("common:countryComboBox.placeholder")}
          </div>
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start" side="bottom">
        <Command>
          <CommandInput placeholder={t("common:countryComboBox.search")} />
          <CommandList>
            <CommandEmpty>{t("common:countryComboBox.notFound")}</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name}`}
                  onSelect={() => {
                    onValueChange?.(country.code);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === country.code ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <FlagIcon countryCodes={[country.code]} />
                  {country.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
